import React, { useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  List,
  useTable,
  ShowButton,
  EditButton,
  DeleteButton,
} from '@refinedev/antd';
import { Table, Space, Tag, Input } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import type { HealthSyncRecordAdminDto } from '@wellverse/shared-ts';
import {
  HealthSyncCategory,
  HealthSyncPlatform,
} from '@wellverse/shared-ts';

const platformColors: Record<HealthSyncPlatform, string> = {
  [HealthSyncPlatform.APPLE_HEALTH]: '#52c41a',
  [HealthSyncPlatform.HEALTH_CONNECT]: '#1890ff',
  [HealthSyncPlatform.PARTNER]: '#722ed1',
};

const categoryColors: Record<HealthSyncCategory, string> = {
  [HealthSyncCategory.CHARACTERISTIC]: '#898989',
  [HealthSyncCategory.BODY]: '#1890ff',
  [HealthSyncCategory.VITAL]: '#ff6b6b',
  [HealthSyncCategory.ACTIVITY]: '#3ecf8e',
  [HealthSyncCategory.NUTRITION]: '#f5a623',
  [HealthSyncCategory.SLEEP]: '#722ed1',
  [HealthSyncCategory.WORKOUT]: '#13c2c2',
  [HealthSyncCategory.OTHER]: '#595959',
};

const tagStyle = (color: string) => ({
  borderColor: color,
  color,
  background: `${color}15`,
  borderRadius: 9999,
  padding: '2px 12px',
  fontWeight: 500,
});

const truncateId = (id: string) =>
  id.length <= 12 ? id : `${id.slice(0, 8)}...${id.slice(-4)}`;

export const HealthSyncRecordList: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const userIdFromUrl = searchParams.get('userId') ?? '';
  const [searchText, setSearchText] = React.useState(
    searchParams.get('search') ?? '',
  );

  const permanentFilters = useMemo(
    () =>
      [
        ...(userIdFromUrl
          ? [{ field: 'userId', operator: 'eq' as const, value: userIdFromUrl }]
          : []),
        ...(searchText
          ? [{ field: 'search', operator: 'contains' as const, value: searchText }]
          : []),
      ],
    [userIdFromUrl, searchText],
  );

  const { tableProps } = useTable<HealthSyncRecordAdminDto>({
    resource: 'health-sync-records',
    filters: { permanent: permanentFilters },
    pagination: { pageSize: 20 },
    queryOptions: { keepPreviousData: true },
  });

  const handleSearch = (value: string) => {
    setSearchText(value);
    const next = new URLSearchParams(searchParams);
    if (value) {
      next.set('search', value);
    } else {
      next.delete('search');
    }
    setSearchParams(next);
  };

  return (
    <List resource="health-sync-records">
      {userIdFromUrl && (
        <div style={{ marginBottom: 12, fontSize: 13 }}>
          Filtered by user:{' '}
          <Link to={`/users/show/${userIdFromUrl}`}>{truncateId(userIdFromUrl)}</Link>
          {' · '}
          <Link to="/health-sync-records">Clear filter</Link>
        </div>
      )}
      <div style={{ marginBottom: 16 }}>
        <Input.Search
          prefix={<SearchOutlined />}
          placeholder="Search external key, source, data type, or user..."
          defaultValue={searchText}
          onSearch={handleSearch}
          allowClear
          style={{ width: 360 }}
        />
      </div>
      <Table {...tableProps} rowKey="id" scroll={{ x: 1400 }}>
        <Table.Column
          dataIndex="userFullName"
          title="User"
          render={(_: string, record: HealthSyncRecordAdminDto) => (
            <Space direction="vertical" size={0}>
              <Link to={`/users/show/${record.userId}`}>
                {record.userFullName || truncateId(record.userId)}
              </Link>
              {record.userEmail && (
                <span style={{ fontSize: 12, opacity: 0.65 }}>{record.userEmail}</span>
              )}
            </Space>
          )}
        />
        <Table.Column
          dataIndex="platform"
          title="Platform"
          filters={Object.values(HealthSyncPlatform).map((p) => ({
            text: p,
            value: p,
          }))}
          render={(platform: HealthSyncPlatform) => (
            <Tag style={tagStyle(platformColors[platform] || '#898989')}>
              {platform}
            </Tag>
          )}
        />
        <Table.Column
          dataIndex="category"
          title="Category"
          filters={Object.values(HealthSyncCategory).map((c) => ({
            text: c,
            value: c,
          }))}
          render={(category: HealthSyncCategory) => (
            <Tag style={tagStyle(categoryColors[category] || '#898989')}>
              {category}
            </Tag>
          )}
        />
        <Table.Column dataIndex="dataType" title="Data Type" />
        <Table.Column
          dataIndex="numericValue"
          title="Value"
          render={(value: number | undefined, record: HealthSyncRecordAdminDto) =>
            value != null ? `${value}${record.unit ? ` ${record.unit}` : ''}` : '—'
          }
        />
        <Table.Column dataIndex="sourceName" title="Source" />
        <Table.Column
          dataIndex="startTime"
          title="Start Time"
          render={(date: string) => (date ? new Date(date).toLocaleString() : '—')}
        />
        <Table.Column
          title="Actions"
          dataIndex="actions"
          fixed="right"
          render={(_: unknown, record: HealthSyncRecordAdminDto) => (
            <Space>
              <ShowButton
                hideText
                size="small"
                recordItemId={record.id}
                resource="health-sync-records"
              />
              <EditButton
                hideText
                size="small"
                recordItemId={record.id}
                resource="health-sync-records"
              />
              <DeleteButton
                hideText
                size="small"
                recordItemId={record.id}
                resource="health-sync-records"
              />
            </Space>
          )}
        />
      </Table>
    </List>
  );
};
